import { categories } from '@/mocks/homeData';

type FeedItem = {
  id: string;
  title: string;
  summary: string;
  category: string;
  categoryName: string;
  author: string;
  time: string;
  readTime: string;
  heat: number;
};

type LatestSectionProps = {
  items: FeedItem[];
  total: number;
  activeCategory: string;
  onCategoryChange: (id: string) => void;
  query: string;
  onQueryChange: (value: string) => void;
  visibleCount: number;
  onLoadMore: () => void;
};

const formatHeat = (value: number) => (value >= 10000 ? `${(value / 10000).toFixed(1)}万` : `${value}`);

export default function LatestSection({
  items,
  total,
  activeCategory,
  onCategoryChange,
  query,
  onQueryChange,
  visibleCount,
  onLoadMore,
}: LatestSectionProps) {
  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return (
    <section id="latest" aria-labelledby="latest-title" className="scroll-mt-28 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-6 border-b border-background-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs tracking-[0.25em] text-primary-600">LATEST UPDATES</p>
            <h2
              id="latest-title"
              className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-4xl"
            >
              最新更新
            </h2>
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
            <label htmlFor="feed-search" className="sr-only">
              搜索爆料关键词
            </label>
            <div className="relative w-full sm:w-72">
              <i
                className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                aria-hidden="true"
              />
              <input
                id="feed-search"
                type="search"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="搜索关键词,如「带货」「时间线」"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => onCategoryChange(cat.id)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-foreground-500">
            共 {items.length} 条 · 全站 {total} 条
          </span>
        </div>

        <ul className="mt-6 divide-y divide-background-200 border-y border-background-200">
          {visibleItems.map((item) => (
            <li key={item.id}>
              <article className="group flex flex-col gap-3 py-6 md:flex-row md:items-center md:gap-8">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-secondary-800">
                      {item.categoryName}
                    </span>
                    <span className="flex items-center gap-1.5 text-foreground-500">
                      <i className="ri-time-line" aria-hidden="true" />
                      {item.time}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:w-56 md:justify-end">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" aria-hidden="true" />
                    {item.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-book-open-line" aria-hidden="true" />
                    {item.readTime}
                  </span>
                  <span className="flex items-center gap-1.5 text-primary-600">
                    <i className="ri-fire-line" aria-hidden="true" />
                    {formatHeat(item.heat)}
                  </span>
                </div>
              </article>
            </li>
          ))}
        </ul>

        {items.length === 0 && (
          <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
              <i className="ri-search-eye-line" aria-hidden="true" />
            </span>
            <p className="font-heading text-base font-bold text-foreground-900">没有找到匹配的内容</p>
            <p className="text-sm text-foreground-500">换个关键词,或点击「全部」看看最近更新了什么。</p>
          </div>
        )}

        {hasMore && items.length > 0 && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={onLoadMore}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              加载更多内容
            </button>
          </div>
        )}
      </div>
    </section>
  );
}